import { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb'
import { Separator } from '@/components/ui/separator'
import { SidebarTrigger } from '@/components/ui/sidebar'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { formatDate } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useCompanyExpensesStore } from '@/stores/company-expenses-store'
import type { CreateExpensePayload } from '@/types/company-expenses'
import { fetchWorkers } from '@/services/workers'
import type { Worker } from '@/types/workers'
import {
  Plus,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Wallet,
  Search,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from 'lucide-react'

type SortField =
  | 'revenue_type'
  | 'description'
  | 'expense_type'
  | 'admin'
  | 'created_at'
  | 'amount'
type SortDirection = 'asc' | 'desc' | null

function LoadingSkeleton() {
  return (
    <div className='space-y-2'>
      {[...Array(5)].map((_, i) => (
        <Skeleton key={i} className='h-12 w-full' />
      ))}
    </div>
  )
}

export default function CompanyExpenses() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { expenses, isLoading, isCreating, error, fetch, create } =
    useCompanyExpensesStore()

  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const [formData, setFormData] = useState<CreateExpensePayload>({
    amount: '',
    revenue_type: 'outcome',
    expense_type: 'company',
    description: '',
  })

  // Filter state
  const [searchQuery, setSearchQuery] = useState('')
  const [filterRevenueType, setFilterRevenueType] = useState<string>('all')
  const [filterExpenseType, setFilterExpenseType] = useState<string>('all')

  // Sort state
  const [sortField, setSortField] = useState<SortField>('created_at')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')

  // Workers state for salary expenses
  const [workers, setWorkers] = useState<Worker[]>([])
  const [isLoadingWorkers, setIsLoadingWorkers] = useState(false)

  useEffect(() => {
    fetch()
  }, [fetch])

  // Fetch workers when needed for salary expenses
  useEffect(() => {
    if (
      isSheetOpen &&
      formData.expense_type === 'salary' &&
      workers.length === 0
    ) {
      setIsLoadingWorkers(true)
      fetchWorkers()
        .then((data) => setWorkers(data))
        .catch((err) => console.error('Failed to fetch workers:', err))
        .finally(() => setIsLoadingWorkers(false))
    }
  }, [isSheetOpen, formData.expense_type, workers.length])

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Cycle through: asc -> desc -> created_at (default)
      if (sortDirection === 'asc') {
        setSortDirection('desc')
      } else if (sortDirection === 'desc') {
        setSortField('created_at')
        setSortDirection('desc')
      } else {
        setSortDirection('asc')
      }
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className='ml-1 h-3 w-3' />
    }
    if (sortDirection === 'asc') {
      return <ArrowUp className='ml-1 h-3 w-3' />
    }
    if (sortDirection === 'desc') {
      return <ArrowDown className='ml-1 h-3 w-3' />
    }
    return <ArrowUpDown className='ml-1 h-3 w-3' />
  }

  // Filtered and sorted expenses
  const filteredExpenses = useMemo(() => {
    let result = expenses.filter((expense) => {
      // Search filter
      const searchLower = searchQuery.toLowerCase()
      const matchesSearch =
        searchQuery === '' ||
        expense.description?.toLowerCase().includes(searchLower) ||
        expense.admin?.first_name.toLowerCase().includes(searchLower) ||
        expense.admin?.last_name.toLowerCase().includes(searchLower) ||
        expense.amount.includes(searchQuery)

      // Revenue type filter
      const matchesRevenueType =
        filterRevenueType === 'all' ||
        expense.revenue_type === filterRevenueType

      // Expense type filter
      const matchesExpenseType =
        filterExpenseType === 'all' ||
        expense.expense_type === filterExpenseType

      return matchesSearch && matchesRevenueType && matchesExpenseType
    })

    // Apply sorting
    if (sortField && sortDirection) {
      result = [...result].sort((a, b) => {
        let aValue: string | number
        let bValue: string | number

        switch (sortField) {
          case 'revenue_type':
            aValue = a.revenue_type
            bValue = b.revenue_type
            break
          case 'description':
            aValue = a.description || ''
            bValue = b.description || ''
            break
          case 'expense_type':
            aValue = a.expense_type
            bValue = b.expense_type
            break
          case 'admin':
            aValue = a.admin ? `${a.admin.first_name} ${a.admin.last_name}` : ''
            bValue = b.admin ? `${b.admin.first_name} ${b.admin.last_name}` : ''
            break
          case 'created_at':
            aValue = new Date(a.created_at).getTime()
            bValue = new Date(b.created_at).getTime()
            break
          case 'amount':
            // Consider revenue_type: income is positive, outcome is negative
            aValue =
              a.revenue_type === 'income'
                ? parseFloat(a.amount)
                : -parseFloat(a.amount)
            bValue =
              b.revenue_type === 'income'
                ? parseFloat(b.amount)
                : -parseFloat(b.amount)
            break
          default:
            return 0
        }

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          const comparison = aValue.localeCompare(bValue)
          return sortDirection === 'asc' ? comparison : -comparison
        }

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
        return 0
      })
    }

    return result
  }, [
    expenses,
    searchQuery,
    filterRevenueType,
    filterExpenseType,
    sortField,
    sortDirection,
  ])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const payload: CreateExpensePayload = {
      ...formData,
      description: formData.description || '',
    }

    const success = await create(payload)
    if (success) {
      setIsSheetOpen(false)
      setFormData({
        amount: '',
        revenue_type: 'outcome',
        expense_type: 'company',
        description: '',
      })
    }
  }

  const clearFilters = () => {
    setSearchQuery('')
    setFilterRevenueType('all')
    setFilterExpenseType('all')
  }

  const hasActiveFilters =
    searchQuery !== '' ||
    filterRevenueType !== 'all' ||
    filterExpenseType !== 'all'

  const formatCurrency = (amount: string | number) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
    }).format(num)
  }

  // const formatDate = (dateString: string) => {
  //   return new Date(dateString).toLocaleDateString('en-US', {
  //     year: 'numeric',
  //     month: 'short',
  //     day: 'numeric',
  //     hour: '2-digit',
  //     minute: '2-digit',
  //   })
  // }

  const totalIncome = expenses
    .filter((e) => e.revenue_type === 'income')
    .reduce((sum, e) => sum + parseFloat(e.amount), 0)

  const totalExpense = expenses
    .filter((e) => e.revenue_type === 'outcome')
    .reduce((sum, e) => sum + parseFloat(e.amount), 0)

  const balance = totalIncome - totalExpense

  return (
    <>
      <header className='flex h-16 shrink-0 items-center gap-2'>
        <div className='flex items-center gap-2 px-4'>
          <SidebarTrigger className='-ml-1' />
          <Separator
            orientation='vertical'
            className='mr-2 data-[orientation=vertical]:h-4'
          />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbPage>{t('expenses.companyExpenses')}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </header>

      {error && (
        <div className='mx-4 mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400'>
          <AlertCircle className='h-4 w-4' />
          <span>{error}</span>
        </div>
      )}

      <div className='flex flex-1 flex-col gap-4 p-4 pt-0'>
        {/* Summary Cards */}
        <div className='grid gap-4 md:grid-cols-3'>
          <Card>
            <CardHeader className='flex flex-row items-center justify-between pb-2'>
              <CardTitle className='text-sm font-medium'>
                {t('expenses.totalIncome')}
              </CardTitle>
              <TrendingUp className='h-4 w-4 text-green-500' />
            </CardHeader>
            <CardContent>
              <div className='text-2xl font-bold text-green-600 dark:text-green-400'>
                {formatCurrency(totalIncome)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className='flex flex-row items-center justify-between pb-2'>
              <CardTitle className='text-sm font-medium'>
                {t('expenses.totalExpenses')}
              </CardTitle>
              <TrendingDown className='h-4 w-4 text-red-500' />
            </CardHeader>
            <CardContent>
              <div className='text-2xl font-bold text-red-600 dark:text-red-400'>
                {formatCurrency(totalExpense)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className='flex flex-row items-center justify-between pb-2'>
              <CardTitle className='text-sm font-medium'>
                {t('expenses.balance')}
              </CardTitle>
              <Wallet className='h-4 w-4 text-muted-foreground' />
            </CardHeader>
            <CardContent>
              <div
                className={`text-2xl font-bold ${
                  balance >= 0
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-red-600 dark:text-red-400'
                }`}
              >
                {formatCurrency(balance)}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Expenses Table */}
        <Card>
          <CardHeader className='flex flex-row items-center justify-between'>
            <div>
              <CardTitle>{t('expenses.title')}</CardTitle>
              <CardDescription>{t('expenses.manageExpenses')}</CardDescription>
            </div>
            <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
              <SheetTrigger asChild>
                <Button>
                  <Plus className='mr-2 h-4 w-4' />
                  {t('expenses.addExpense')}
                </Button>
              </SheetTrigger>
              <SheetContent>
                <SheetHeader>
                  <SheetTitle>{t('expenses.addNewExpense')}</SheetTitle>
                  <SheetDescription>
                    {t('expenses.createNewExpense')}
                  </SheetDescription>
                </SheetHeader>
                <form
                  onSubmit={handleSubmit}
                  className='flex flex-col gap-4 p-4'
                >
                  <div className='space-y-2'>
                    <Label htmlFor='amount'>{t('expenses.amount')}</Label>
                    <Input
                      id='amount'
                      type='number'
                      step='0.01'
                      placeholder='0.00'
                      value={formData.amount}
                      onChange={(e) =>
                        setFormData({ ...formData, amount: e.target.value })
                      }
                      required
                    />
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor='revenue_type'>
                      {t('expenses.revenueType')}
                    </Label>
                    <Select
                      value={formData.revenue_type}
                      onValueChange={(value: 'income' | 'outcome') =>
                        setFormData({ ...formData, revenue_type: value })
                      }
                    >
                      <SelectTrigger className='w-full'>
                        <SelectValue placeholder={t('expenses.selectType')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='income'>
                          {t('expenses.income')}
                        </SelectItem>
                        <SelectItem value='outcome'>
                          {t('expenses.expense')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor='expense_type'>
                      {t('expenses.expenseType')}
                    </Label>
                    <Select
                      value={formData.expense_type}
                      onValueChange={(value: 'company' | 'salary') =>
                        setFormData({
                          ...formData,
                          expense_type: value,
                          worker:
                            value !== 'salary' ? undefined : formData.worker,
                        })
                      }
                    >
                      <SelectTrigger className='w-full'>
                        <SelectValue
                          placeholder={t('expenses.selectCategory')}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='company'>
                          {t('expenses.company')}
                        </SelectItem>
                        <SelectItem value='salary'>
                          {t('expenses.salary')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.expense_type === 'salary' && (
                    <div className='space-y-2'>
                      <Label htmlFor='worker'>{t('expenses.worker')}</Label>
                      <Select
                        value={formData.worker?.toString() || ''}
                        onValueChange={(value) =>
                          setFormData({
                            ...formData,
                            worker: value ? parseInt(value) : null,
                          })
                        }
                        disabled={isLoadingWorkers}
                      >
                        <SelectTrigger className='w-full'>
                          <SelectValue
                            placeholder={
                              isLoadingWorkers
                                ? t('expenses.loadingWorkers')
                                : t('expenses.selectWorker')
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {workers.map((worker) => (
                            <SelectItem
                              key={worker.id}
                              value={worker.id.toString()}
                            >
                              {worker.account.first_name}{' '}
                              {worker.account.last_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className='space-y-2'>
                    <Label htmlFor='description'>
                      {t('expenses.descriptionOptional')}
                    </Label>
                    <Textarea
                      id='description'
                      placeholder={t('expenses.enterDescription')}
                      value={formData.description || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          description: e.target.value,
                        })
                      }
                    />
                  </div>

                  <SheetFooter>
                    <Button type='submit' disabled={isCreating}>
                      {isCreating ? t('common.loading') : t('common.create')}
                    </Button>
                  </SheetFooter>
                </form>
              </SheetContent>
            </Sheet>
          </CardHeader>
          <CardContent>
            {/* Filters */}
            <div className='mb-4 flex flex-wrap items-center gap-3'>
              <div className='relative flex-1 min-w-[200px]'>
                <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
                <Input
                  placeholder={t('expenses.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className='pl-9'
                />
              </div>
              <Select
                value={filterRevenueType}
                onValueChange={setFilterRevenueType}
              >
                <SelectTrigger className='w-[140px]'>
                  <SelectValue placeholder={t('expenses.revenueType')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>{t('expenses.allTypes')}</SelectItem>
                  <SelectItem value='income'>{t('expenses.income')}</SelectItem>
                  <SelectItem value='outcome'>
                    {t('expenses.expense')}
                  </SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={filterExpenseType}
                onValueChange={setFilterExpenseType}
              >
                <SelectTrigger className='w-[140px]'>
                  <SelectValue placeholder={t('expenses.category')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>
                    {t('expenses.allCategories')}
                  </SelectItem>
                  <SelectItem value='company'>
                    {t('expenses.company')}
                  </SelectItem>
                  <SelectItem value='salary'>{t('expenses.salary')}</SelectItem>
                  <SelectItem value='other'>{t('expenses.other')}</SelectItem>
                  <SelectItem value='material'>{t('expenses.material')}</SelectItem>
                </SelectContent>
              </Select>
              {hasActiveFilters && (
                <Button variant='ghost' size='sm' onClick={clearFilters}>
                  <X className='mr-1 h-4 w-4' />
                  {t('common.clear')}
                </Button>
              )}
            </div>

            {isLoading ? (
              <LoadingSkeleton />
            ) : filteredExpenses.length === 0 ? (
              <div className='py-8 text-center text-muted-foreground'>
                {expenses.length === 0
                  ? t('expenses.noExpensesFound')
                  : t('expenses.noExpensesMatch')}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead
                      className='cursor-pointer select-none hover:bg-muted/50'
                      onClick={() => handleSort('revenue_type')}
                    >
                      <div className='flex items-center'>
                        {t('expenses.type')}
                        {getSortIcon('revenue_type')}
                      </div>
                    </TableHead>
                    <TableHead
                      className='cursor-pointer select-none hover:bg-muted/50'
                      onClick={() => handleSort('description')}
                    >
                      <div className='flex items-center'>
                        {t('expenses.description')}
                        {getSortIcon('description')}
                      </div>
                    </TableHead>
                    <TableHead
                      className='cursor-pointer select-none hover:bg-muted/50'
                      onClick={() => handleSort('expense_type')}
                    >
                      <div className='flex items-center'>
                        {t('expenses.category')}
                        {getSortIcon('expense_type')}
                      </div>
                    </TableHead>
                    <TableHead
                      className='cursor-pointer select-none hover:bg-muted/50'
                      onClick={() => handleSort('admin')}
                    >
                      <div className='flex items-center'>
                        {t('expenses.admin')}
                        {getSortIcon('admin')}
                      </div>
                    </TableHead>
                    <TableHead
                      className='cursor-pointer select-none hover:bg-muted/50'
                      onClick={() => handleSort('created_at')}
                    >
                      <div className='flex items-center'>
                        {t('expenses.date')}
                        {getSortIcon('created_at')}
                      </div>
                    </TableHead>
                    <TableHead
                      className='cursor-pointer select-none hover:bg-muted/50 text-right'
                      onClick={() => handleSort('amount')}
                    >
                      <div className='flex items-center justify-end'>
                        {t('expenses.amount')}
                        {getSortIcon('amount')}
                      </div>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredExpenses.map((expense) => (
                    <TableRow
                      key={expense.id}
                      className='cursor-pointer'
                      onClick={() =>
                        navigate(`/company-expenses/${expense.id}`)
                      }
                    >
                      <TableCell>
                        <div className='flex items-center gap-2'>
                          {expense.revenue_type === 'income' ? (
                            <TrendingUp className='h-4 w-4 text-green-500' />
                          ) : (
                            <TrendingDown className='h-4 w-4 text-red-500' />
                          )}
                          <span className='capitalize'>
                            {expense.revenue_type === 'income'
                              ? t('expenses.income')
                              : t('expenses.expense')}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {expense.description || (
                          <span className='text-muted-foreground'>-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className='rounded-full bg-muted px-2 py-1 text-xs capitalize'>
                          {expense.expense_type === 'company'
                            ? t('expenses.company')
                            : expense.expense_type === 'salary'
                            ? t('expenses.salary')
                            : expense.expense_type === 'material'
                            ? t('expenses.material')
                            : t('expenses.other')}
                        </span>
                      </TableCell>
                      <TableCell>
                        {expense.admin
                          ? `${expense.admin.first_name} ${expense.admin.last_name}`
                          : '-'}
                      </TableCell>
                      <TableCell className='text-muted-foreground'>
                        {formatDate(expense.created_at)}
                      </TableCell>
                      <TableCell className='text-right'>
                        <span
                          className={`font-semibold ${
                            expense.revenue_type === 'income'
                              ? 'text-green-600 dark:text-green-400'
                              : 'text-red-600 dark:text-red-400'
                          }`}
                        >
                          {expense.revenue_type === 'income' ? '+' : '-'}
                          {formatCurrency(expense.amount)}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {/* Results count and total */}
            {!isLoading && expenses.length > 0 && (
              <div className='mt-4 flex items-center justify-between text-sm text-muted-foreground'>
                <span>
                  {t('common.showing')} {filteredExpenses.length} {t('common.of')}{' '}
                  {expenses.length}
                </span>
                {(() => {
                  const total = filteredExpenses.reduce((sum, e) => {
                    const amount = parseFloat(e.amount)
                    return e.revenue_type === 'income'
                      ? sum + amount
                      : sum - amount
                  }, 0)
                  return (
                    <span className='font-medium'>
                      {t('common.total')}:{' '}
                      <span
                        className={
                          total > 0
                            ? 'text-green-600 dark:text-green-400'
                            : total < 0
                            ? 'text-red-600 dark:text-red-400'
                            : 'text-muted-foreground'
                        }
                      >
                        {formatCurrency(total)}
                      </span>
                    </span>
                  )
                })()}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  )
}
